<template>
  <div class="profile-tab">
    <div class="profile-card">
      <div class="profile-card-header">
        <h2>My information</h2>
        <p>Update your public profile details.</p>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="form-grid">
          <b-field label="Full name" :type="errors.name ? 'is-danger' : null" :message="errors.name">
            <b-input v-model="form.name" required maxlength="255" placeholder="Your full name" />
          </b-field>

          <b-field label="Participant code">
            <b-input :value="'#' + (form.item_index || '')" disabled />
          </b-field>

          <b-field label="Email" :type="errors.email ? 'is-danger' : null" :message="errors.email">
            <b-input v-model="form.email" type="email" required placeholder="email@example.com" />
          </b-field>

          <b-field label="Phone" :type="errors.phone ? 'is-danger' : null" :message="errors.phone">
            <b-input v-model="form.phone" placeholder="+250..." maxlength="30" />
          </b-field>

          <b-field label="Bio" class="full-width" :type="errors.bio ? 'is-danger' : null" :message="errors.bio">
            <b-input v-model="form.bio" type="textarea" maxlength="2000" placeholder="Tell voters about yourself" />
          </b-field>

          <b-field label="Profile photo" class="full-width">
            <upload-trigger
              :open="false"
              :enableinput="false"
              :required="false"
              :multiple="false"
              location="assets/uploaded/"
              :url="$store.state.ASSETS_BASE_URL"
              :uploadurl="$store.state.BASE_URL + 'upload'"
              name="participant-profile-image"
              :value="form.profile_image"
              @onFileSelected="onImageSelected"
            >
              <div class="image-upload-widget">
                <img
                  v-if="form.profile_image"
                  :src="$store.state.ASSETS_BASE_URL + '/assets/uploaded/' + form.profile_image"
                  alt="Profile preview"
                />
                <span v-else>Choose image to upload</span>
              </div>
            </upload-trigger>
          </b-field>
        </div>

        <div class="social-section">
          <h3>Social media links</h3>
          <div class="form-grid">
            <b-field label="Facebook URL" class="social-url">
              <b-input v-model="form.facebook_url" placeholder="https://facebook.com/username" />
            </b-field>
            <b-field label="Facebook followers">
              <b-numberinput v-model="form.facebook_followers" expanded min="0" controls-position="compact" />
            </b-field>

            <b-field label="Twitter / X URL" class="social-url">
              <b-input v-model="form.twitter_url" placeholder="https://twitter.com/username" />
            </b-field>
            <b-field label="Twitter followers">
              <b-numberinput v-model="form.twitter_followers" expanded min="0" controls-position="compact" />
            </b-field>

            <b-field label="Instagram URL" class="social-url">
              <b-input v-model="form.instagram_url" placeholder="https://instagram.com/username" />
            </b-field>
            <b-field label="Instagram followers">
              <b-numberinput v-model="form.instagram_followers" expanded min="0" controls-position="compact" />
            </b-field>

            <b-field label="Threads URL" class="social-url">
              <b-input v-model="form.threads_url" placeholder="https://threads.net/username" />
            </b-field>
            <b-field label="Threads followers">
              <b-numberinput v-model="form.threads_followers" expanded min="0" controls-position="compact" />
            </b-field>

            <b-field label="TikTok URL" class="social-url">
              <b-input v-model="form.tiktok_url" placeholder="https://tiktok.com/@username" />
            </b-field>
            <b-field label="TikTok followers">
              <b-numberinput v-model="form.tiktok_followers" expanded min="0" controls-position="compact" />
            </b-field>

            <b-field label="YouTube URL" class="social-url">
              <b-input v-model="form.youtube_url" placeholder="https://youtube.com/channel/..." />
            </b-field>
            <b-field label="YouTube subscribers">
              <b-numberinput v-model="form.youtube_followers" expanded min="0" controls-position="compact" />
            </b-field>
          </div>
        </div>

        <div v-if="formError" class="form-error">{{ formError }}</div>

        <div class="form-actions">
          <b-button type="is-light" @click="resetForm" :disabled="saving">Reset</b-button>
          <b-button type="is-primary" native-type="submit" :loading="saving">Save changes</b-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
const emptyForm = () => ({
  name: '',
  email: '',
  phone: '',
  bio: '',
  profile_image: '',
  item_index: '',
  facebook_url: '',
  facebook_followers: 0,
  twitter_url: '',
  twitter_followers: 0,
  instagram_url: '',
  instagram_followers: 0,
  threads_url: '',
  threads_followers: 0,
  tiktok_url: '',
  tiktok_followers: 0,
  youtube_url: '',
  youtube_followers: 0,
})

export default {
  name: 'ProfileTab',
  props: {
    participant: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      form: emptyForm(),
      saving: false,
      formError: null,
      errors: {},
    }
  },
  watch: {
    participant: {
      immediate: true,
      deep: true,
      handler(value) {
        this.applyParticipant(value)
      },
    },
  },
  methods: {
    applyParticipant(participant) {
      if (!participant || !participant.id) return
      this.form = {
        ...emptyForm(),
        name: participant.name || '',
        email: participant.email || '',
        phone: participant.phone || '',
        bio: participant.bio || '',
        profile_image: participant.profile_image || participant.image || '',
        item_index: participant.item_index,
        facebook_url: participant.facebook_url || '',
        facebook_followers: Number(participant.facebook_followers) || 0,
        twitter_url: participant.twitter_url || '',
        twitter_followers: Number(participant.twitter_followers) || 0,
        instagram_url: participant.instagram_url || '',
        instagram_followers: Number(participant.instagram_followers) || 0,
        threads_url: participant.threads_url || '',
        threads_followers: Number(participant.threads_followers) || 0,
        tiktok_url: participant.tiktok_url || '',
        tiktok_followers: Number(participant.tiktok_followers) || 0,
        youtube_url: participant.youtube_url || '',
        youtube_followers: Number(participant.youtube_followers) || 0,
      }
    },
    onImageSelected(file) {
      this.form.profile_image = file
    },
    resetForm() {
      this.applyParticipant(this.participant)
      this.formError = null
      this.errors = {}
    },
    buildPayload() {
      const {
        name,
        email,
        phone,
        bio,
        profile_image,
        facebook_url,
        facebook_followers,
        twitter_url,
        twitter_followers,
        instagram_url,
        instagram_followers,
        threads_url,
        threads_followers,
        tiktok_url,
        tiktok_followers,
        youtube_url,
        youtube_followers,
      } = this.form
      const payload = {
        name,
        email,
        phone,
        bio,
        profile_image,
        facebook_url: facebook_url || '',
        facebook_followers,
        twitter_url: twitter_url || '',
        twitter_followers,
        instagram_url: instagram_url || '',
        instagram_followers,
        threads_url: threads_url || '',
        threads_followers,
        tiktok_url: tiktok_url || '',
        tiktok_followers,
        youtube_url: youtube_url || '',
        youtube_followers,
      }
      return payload
    },
    async handleSubmit() {
      this.saving = true
      this.formError = null
      this.errors = {}
      try {
        const response = await this.$store.dispatch('PARTICIPANT_UPDATE_PROFILE', this.buildPayload())
        if (response.data.status === 'ok') {
          const updated = response.data.data.participant
          this.$buefy.toast.open({ message: 'Profile updated successfully', type: 'is-success' })
          this.$emit('updated', updated)
          localStorage.setItem('participant_data', JSON.stringify(updated))
        } else {
          this.formError = response.data.message || 'Failed to update profile'
        }
      } catch (error) {
        this.formError = (error.response && error.response.data && error.response.data.message)
          || 'Failed to update profile. Please try again.'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-tab {
  .profile-card {
    background: #fff;
    border-radius: 4px;
    padding: 20px;
    border: 1px solid #ddd;

    .profile-card-header {
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid #eee;
      h2 { margin: 0 0 8px; font-size: 20px; color: #1a191b; font-weight: 600; }
      p { margin: 0; color: #797979; font-size: 14px; }
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px 20px;
      .full-width { grid-column: 1 / -1; }
      .social-url { grid-column: span 1; }
    }

    .social-section {
      margin-top: 28px;
      padding-top: 24px;
      border-top: 1px solid #eee;
      h3 { margin: 0 0 16px; font-size: 18px; color: #333; }
    }

    .image-upload-widget {
      border: 2px dashed #ddd;
      border-radius: 8px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      color: #666;
      img {
        max-width: 160px;
        max-height: 160px;
        border-radius: 8px;
        object-fit: cover;
      }
    }

    .form-error {
      margin-top: 16px;
      color: #ff3860;
      font-size: 14px;
    }

    .form-actions {
      margin-top: 24px;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }
  }
}

@media (max-width: 768px) {
  .profile-tab .profile-card .form-grid {
    grid-template-columns: 1fr;
    .social-url { grid-column: auto; }
  }
}
</style>
